// HelpTip.jsx
// The one help affordance on this dashboard (#171).
//
// Before this there were three native `title=` attributes carrying an
// explanation, a handful of inline captions, and nothing at all on most
// objects. A native title does nothing on a phone — no hover, no long-press,
// no way in — so on the layout this project has fixed four times (#44, #78,
// #84, #104) the help simply did not exist.
//
// Two decisions make this reachable rather than merely present:
//
//   * THE TARGET IS VISIBLE. An (i) button, not a long-press on the label.
//     MUI's <Tooltip> does handle touch, but its touch gesture is a 700 ms
//     press on an element that looks like plain text, which nobody discovers.
//     A tooltip only a hover can reach is the current problem rebuilt in a new
//     component.
//
//   * IT IS OPENED BY POINTER TYPE, NOT BY MUI'S LISTENERS. MUI's own
//     listeners are disabled and the open state is controlled here, because a
//     tap on a touch screen also emits a synthetic mouseenter: with hover
//     listeners on, the enter opens the tooltip and the click that follows
//     closes it again, so the first tap appears to do nothing. `pointerType`
//     is the only thing that separates the two, so it is what is read. A mouse
//     opens on enter and closes on leave; a touch opens on tap and toggles on
//     the next one; a keyboard opens on focus. Escape closes it in every case —
//     MUI binds that whenever `open` is true and calls `onClose`.
//
// The body is a list of translation keys from helpTopics.js, never prose passed
// in at the call site. See that file for why.

import React, { useRef, useState } from "react";
import { Box, ClickAwayListener, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { HELP_ARIA_KEY, HELP_TOPICS, helpBodyKeys } from "../helpTopics";

// Wide enough that a four-sentence caveat is not a column of two-word lines,
// and bounded so it cannot run off a 320 px phone.
const MAX_WIDTH = 320;

export default function HelpTip({
  topic,
  t = (k) => k,
  simulated = false,
  vars = {},
  extraKeys = [],
  placement = "bottom",
  sx = {},
}) {
  const [open, setOpen] = useState(false);
  // Which device opened the last interaction. Set on pointerdown, which fires
  // before click on every pointer type, so the click handler can tell a tap
  // from a mouse press.
  const pointer = useRef("mouse");
  // Whether the focus the button is about to receive came from a pointer.
  //
  // A browser focuses a <button> as part of clicking it, and that focus lands
  // BEFORE the click. So "open on focus" and "toggle on click" cancel each
  // other out inside a single tap: the tooltip opened and shut again before the
  // finger left the glass, and the control needed two taps to do anything. That
  // is the defect this ref exists to prevent, and HelpTip.test.jsx fires the
  // full pointerdown → focus → click sequence a browser really emits so it
  // cannot come back.
  //
  // It is also why blur does not close a tap-opened tooltip. Whether a tap
  // focuses a button at all differs between iOS and Android, and on iOS the
  // focus can be taken away again a moment later — closing on that would make
  // the tooltip vanish on its own. For a tap, ClickAwayListener is what closes
  // it; blur is only for the keyboard.
  const focusFromPointer = useRef(false);

  const definition = HELP_TOPICS[topic];
  // An unregistered topic renders nothing rather than throwing: a typo in a
  // help id must not take a panel down. helpTopics.test.js is what catches it.
  if (!definition) return null;

  const keys = helpBodyKeys(topic, { simulated, extraKeys });
  const label = t(definition.labelKey);

  const isMouse = (event) => (event.pointerType || "mouse") === "mouse";

  const body = (
    <Box component="span" sx={{ display: "block" }}>
      {keys.map((key, i) => (
        <Box
          key={key}
          component="span"
          sx={{
            display: "block",
            mt: i === 0 ? 0 : 1,
            // The simulated lead is the first line and is weighted, because it
            // is the fact that changes what every line under it means.
            fontWeight: simulated && i === 0 ? 700 : 400,
            fontSize: 12.5,
            lineHeight: 1.45,
          }}
        >
          {t(key, vars)}
        </Box>
      ))}
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      {/* The listener needs one child that holds a ref; Tooltip's own child is
          the button, so this span is what it attaches to. */}
      <Box component="span" sx={{ display: "inline-flex", ...sx }}>
        <Tooltip
          open={open}
          onClose={() => setOpen(false)}
          title={body}
          placement={placement}
          arrow
          disableFocusListener
          disableHoverListener
          disableTouchListener
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: MAX_WIDTH,
                bgcolor: "rgba(17,24,39,0.96)",
                p: 1.25,
              },
            },
            arrow: { sx: { color: "rgba(17,24,39,0.96)" } },
          }}
        >
          <IconButton
            type="button"
            aria-label={t(HELP_ARIA_KEY, { label })}
            size="small"
            onPointerDown={(e) => {
              pointer.current = e.pointerType || "mouse";
              focusFromPointer.current = true;
            }}
            onPointerEnter={(e) => { if (isMouse(e)) setOpen(true); }}
            onPointerLeave={(e) => { if (isMouse(e)) setOpen(false); }}
            // Keyboard only — see focusFromPointer above.
            onFocus={() => { if (!focusFromPointer.current) setOpen(true); }}
            onBlur={() => {
              if (!focusFromPointer.current) setOpen(false);
              // Reset here rather than on click: the button keeps focus after a
              // tap, and without this a control that had once been tapped could
              // never be opened from the keyboard again.
              focusFromPointer.current = false;
            }}
            onClick={(e) => {
              // Several of these sit inside something else that is clickable —
              // an anomaly card that expands, a chip that toggles a map layer.
              // Asking for help must not also fire that.
              e.stopPropagation();
              setOpen((was) => (pointer.current === "mouse" ? true : !was));
            }}
            sx={{
              p: "4px",
              color: "#9ca3af",
              flexShrink: 0,
              "&:hover": { color: "#4b5563", bgcolor: "transparent" },
              // A 26 px target is fine for a cursor and too small for a thumb.
              // Coarse pointers get the padding that takes it past 40 px
              // without changing the icon or the layout on desktop.
              "@media (pointer: coarse)": { p: "11px", m: "-7px" },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
}
